/**
 * SKIN SLOTS — painted textures that take over from the CSS-drawn skin the
 * moment their file exists, with no code change.
 *
 * The button system, panels, modals, toasts, header and the meta background
 * are drawn in CSS today (gradients, rim, lip, gloss). The Higgsfield pass
 * (`docs/ui/HIGGSFIELD-GENERATION-PLAN.md`) paints 9-slice textures for them.
 * Each lands at `/ui/skins/<id>.png`; on first use of the UI sheet this module
 * probes every slot once (an `Image`, so a dev server's index.html-with-a-200
 * fails to decode exactly like a 404) and, for each file that really loads,
 * sets `--rga-skin-<id>` on <html> and the class `has-skin-<id>`. The CSS in
 * SKIN_CSS only applies a texture under that class, so a missing file changes
 * nothing and costs one request per session (misses are remembered in
 * sessionStorage).
 *
 * 9-slice: `slice` is the inset in source pixels that stays unscaled (the
 * corners); `width` is how wide those corners draw on screen.
 */

export interface SkinSlot {
  readonly id: string;
  /** Source size the generation plan asks for. */
  readonly size: string;
  /** 9-slice inset in source px (0 = a plain cover background). */
  readonly slice: number;
  /** Rendered corner width in CSS px. */
  readonly width: number;
  /** The selector whose face the texture replaces. */
  readonly selector: string;
}

export const SKIN_BASE = '/ui/skins/';

export const SKIN_SLOTS: readonly SkinSlot[] = Object.freeze([
  { id: 'btn-primary', size: '384x128', slice: 40, width: 14, selector: '.rga-btn--primary' },
  { id: 'btn-secondary', size: '384x128', slice: 40, width: 14, selector: '.rga-btn--secondary' },
  { id: 'btn-danger', size: '384x128', slice: 40, width: 14, selector: '.rga-btn--danger' },
  { id: 'btn-icon', size: '128x128', slice: 40, width: 14, selector: '.rga-btn--icon' },
  { id: 'btn-pill', size: '256x96', slice: 46, width: 16, selector: '.rga-btn--pill' },
  { id: 'panel-card', size: '512x512', slice: 64, width: 18, selector: '.rga-card' },
  { id: 'panel-modal', size: '768x768', slice: 96, width: 24, selector: '.rga-modal__panel' },
  { id: 'panel-toast', size: '512x160', slice: 48, width: 16, selector: '.rga-toast' },
  { id: 'bar-header', size: '1920x128', slice: 0, width: 0, selector: '[data-testid="meta-header"]' },
  { id: 'bar-tabs', size: '1920x160', slice: 0, width: 0, selector: '[data-testid="meta-tabs"]' },
  { id: 'bg-meta', size: '2560x1440', slice: 0, width: 0, selector: '[data-testid="meta-shell"]' },
]);

const MISSING_KEY = 'rga.ui.skins.missing';
const probed = new WeakSet<Document>();

/** The CSS that applies a texture only once its class is on <html>. */
export function skinCss(): string {
  return SKIN_SLOTS.map((s) =>
    s.slice > 0
      ? `:root.has-skin-${s.id} ${s.selector}{border-style:solid;border-color:transparent;` +
        `border-image:var(--rga-skin-${s.id}) ${s.slice} fill / ${s.width}px round;background:none}`
      : `:root.has-skin-${s.id} ${s.selector}{background-image:var(--rga-skin-${s.id});` +
        `background-size:cover;background-position:center}`,
  ).join('\n');
}

function readMissing(): Set<string> {
  try {
    return new Set((globalThis.sessionStorage?.getItem(MISSING_KEY) ?? '').split(',').filter(Boolean));
  } catch {
    return new Set();
  }
}

function writeMissing(set: Set<string>): void {
  try {
    globalThis.sessionStorage?.setItem(MISSING_KEY, [...set].join(','));
  } catch {
    /* memory only */
  }
}

/**
 * Probe every skin slot once per document. `makeImage` is a test seam.
 * Returns the ids it asked for.
 */
export function probeSkins(
  doc: Document = document,
  makeImage: () => HTMLImageElement | null = () =>
    typeof Image === 'function' ? new Image() : null,
): string[] {
  if (probed.has(doc)) return [];
  probed.add(doc);
  const missing = readMissing();
  const asked: string[] = [];
  for (const slot of SKIN_SLOTS) {
    if (missing.has(slot.id)) continue;
    const img = makeImage();
    if (img === null) break;
    const url = `${SKIN_BASE}${slot.id}.png`;
    img.onload = () => {
      doc.documentElement.style.setProperty(`--rga-skin-${slot.id}`, `url("${url}")`);
      doc.documentElement.classList.add(`has-skin-${slot.id}`);
    };
    img.onerror = () => {
      missing.add(slot.id);
      writeMissing(missing);
    };
    img.src = url;
    asked.push(slot.id);
  }
  return asked;
}
